import React from 'react';
import { TTransactionHistory } from '../../types/TransactionHistory';
import Button from '@/components/Button';
import { ExclamationIcon, ExitIcon, LinkIcon } from '@/components/Icons';
import UploadAttachmentSection from '../UploadAttachmentSection';
import { TAttachment } from '@/services/types/TransactionData';
import { getWorkingTransactionAttachmentUrl } from '@/services/transactionHistory';
import useHomeContext from '@/hooks/useHomeContext';
import { TDeleteAttachmentData } from '../../types/DeleteAttachmentData';

interface AttachmentsSectionProps {
  transaction: TTransactionHistory;
  onDeleteAttachment: (data: TDeleteAttachmentData) => void;
  onFinishUpload?: (attachment: TAttachment) => void;
}

const AttachmentsSection: React.FC<AttachmentsSectionProps> = ({
  transaction,
  onDeleteAttachment: handleDeleteAttachment,
  onFinishUpload
}) => {
  const { setAttachmentType, setAttachmentUrl, setSelectedTransaction } = useHomeContext()!;

  const handleViewAttachment = async (attachment: TAttachment, transactionId: string) => {
    if (attachment.state !== 'dirty') {
      const url = await getWorkingTransactionAttachmentUrl(transactionId, attachment);

      setAttachmentUrl(url);
      setAttachmentType(attachment.type);

      setSelectedTransaction((txn) =>
        txn
          ? {
              ...txn,
              attachments: txn.details?.attachments.map((a) =>
                a.id === attachment.id ? { ...a, url: url } : a
              )
            }
          : txn
      );
    }
  };

  return (
    <>
      {transaction.details?.attachments.map((attachment) => (
        <div key={`attachment-${attachment.id}`} className="mt-3">
          <Button
            className={`rounded-lg w-full justify-start min-h-12 h-auto${
              attachment.state === 'dirty'
                ? ' cursor-default hover:bg-transparent focus:bg-transparent active:bg-transparent focus:ring-transparent text-dark-base-90'
                : ''
            }`}
            childrenClassName="w-full py-0 pr-0"
            variant={'secondary'}
            onClick={() => handleViewAttachment(attachment, transaction.details!.transactionId)}>
            <span>{attachment.state === 'dirty' ? <ExclamationIcon /> : <LinkIcon />}</span>
            <span className="leading-6 text-left" style={{ wordBreak: 'break-all' }}>
              <p>{attachment.name}</p>
              {attachment.state === 'dirty' ? (
                <p className="text-error-darkred text-[0.625rem] leading-3">
                  File is corrupted. Please remove and add a new file.
                </p>
              ) : null}
            </span>
            <>
              <div className="min-h-12 w-px bg-gray-200 flex flex-initial ml-auto -mr-2" />
              <span
                className="hover:text-emerge-navy-2 border-transparent border-[1rem] border-solid cursor-pointer"
                onClick={(e) => {
                  handleDeleteAttachment({
                    transactionId: transaction.details!.transactionId,
                    attachmentId: attachment.id,
                    name: attachment.name,
                    state: attachment.state
                  });
                  e.stopPropagation();
                }}>
                <ExitIcon className="w-3.5 h-3.5" />
              </span>
            </>
          </Button>
        </div>
      ))}
      {transaction.details!.attachments.length < 5 ? (
        <UploadAttachmentSection
          transactionId={transaction.details!.transactionId}
          attachmentCount={transaction.details!.attachments.length ?? 0}
          onFinishUpload={onFinishUpload}
        />
      ) : (
        <div className="text-emerge-navy-2 text-sm mt-4">Max attachments reached</div>
      )}
    </>
  );
};

export default AttachmentsSection;
